import React, {useEffect,useState} from 'react';
import {loadGeoOperationsSummary,loadGeoOperationsTimeline,loadGeoOperationsPresence} from './geoApi.js';

const number=value=>Number(value||0).toLocaleString('en-NG');
const stamp=value=>{
  if(!value||!Number.isFinite(Date.parse(value)))return 'Unknown time';
  return new Date(value).toLocaleString('en-NG',{timeZone:'Africa/Lagos',day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'});
};
const message=err=>err?.response?.data?.message||'Operational intelligence is temporarily unavailable.';
const periods=[{days:1,label:'24 hours'},{days:7,label:'7 days'},{days:30,label:'30 days'}];

/** Read-only, authenticated operations intelligence. Socket events refresh via refreshKey;
 * periodic HTTP refresh keeps the same data accessible if Socket.IO is unavailable. */
export default function GeoLiveOperationsCenter({role,token,refreshKey=0,connected=false,initialExpanded=false}){
  const [tab,setTab]=useState('Summary');
  const [summary,setSummary]=useState(null),[timeline,setTimeline]=useState(null),[presence,setPresence]=useState(null);
  const [days,setDays]=useState(1),[page,setPage]=useState(1);
  const [tick,setTick]=useState(0),[errors,setErrors]=useState({}),[busy,setBusy]=useState({});
  // Keep the opening globe responsive: the CEO also opens heavyweight operations reads on demand.
  const [collapsed,setCollapsed]=useState(!initialExpanded);
  useEffect(()=>{if(collapsed||!token)return undefined;
    const controller=new AbortController();
    setBusy(current=>({...current,summary:true}));
    loadGeoOperationsSummary(role,token,controller.signal).then(data=>{
      if(!controller.signal.aborted){setSummary(data);setErrors(current=>({...current,summary:null}));}
    }).catch(err=>{if(!controller.signal.aborted)setErrors(current=>({...current,summary:message(err)}))})
      .finally(()=>{if(!controller.signal.aborted)setBusy(current=>({...current,summary:false}));});
    return()=>controller.abort();
  },[role,token,refreshKey,tick,collapsed]);
  useEffect(()=>{if(collapsed||tab!=='Timeline'||!token)return undefined;
    const controller=new AbortController();
    setBusy(current=>({...current,timeline:true}));
    loadGeoOperationsTimeline(role,token,{days,page},controller.signal).then(data=>{
      if(!controller.signal.aborted){setTimeline(data);setErrors(current=>({...current,timeline:null}));}
    }).catch(err=>{if(!controller.signal.aborted)setErrors(current=>({...current,timeline:message(err)}))})
      .finally(()=>{if(!controller.signal.aborted)setBusy(current=>({...current,timeline:false}));});
    return()=>controller.abort();
  },[role,token,refreshKey,tick,days,page,tab,collapsed]);
  useEffect(()=>{if(collapsed||tab!=='Presence'||!token)return undefined;
    const controller=new AbortController();
    setBusy(current=>({...current,presence:true}));
    loadGeoOperationsPresence(role,token,controller.signal).then(data=>{
      if(!controller.signal.aborted){setPresence(data);setErrors(current=>({...current,presence:null}));}
    }).catch(err=>{if(!controller.signal.aborted)setErrors(current=>({...current,presence:message(err)}))})
      .finally(()=>{if(!controller.signal.aborted)setBusy(current=>({...current,presence:false}));});
    return()=>controller.abort();
  },[role,token,refreshKey,tick,tab,collapsed]);
  useEffect(()=>{if(collapsed)return undefined;
    const timer=setInterval(()=>{
      if(typeof document==='undefined'||!document.hidden)setTick(value=>value+1);
    },60000);
    return()=>clearInterval(timer);
  },[collapsed]);
  return <section className="ugi-ops" aria-label={`${role} live operations intelligence`}>
    <div className="ugi-ops-top">
      <div><span className="ugi-eyebrow">UNIVERSE OPERATIONS / {role}</span><h2>{role==='CEO'?'Global operations center':'Territory operations center'}</h2>
        <p>Authenticated records, team connections and actionable subscriptions across your permitted territory.</p></div>
      <div className="ugi-ops-actions"><span className="ugi-ops-signal"><i className={connected?'is-connected':''}/>{connected?'Socket connected':'HTTP refresh available'}</span>
        <button type="button" onClick={()=>setCollapsed(value=>!value)} aria-expanded={!collapsed}>{collapsed?'Open operations':'Minimize'}</button>
        {!collapsed&&<button type="button" onClick={()=>setTick(value=>value+1)}>↻ Update</button>}
      </div>
    </div>
    {!collapsed&&<>
      <div className="ugi-ops-tabs" role="group" aria-label="Operations views">
        {['Summary','Timeline','Presence'].map(item=><button key={item} type="button" aria-pressed={tab===item} onClick={()=>setTab(item)}>{item}</button>)}
      </div>
      {errors[tab.toLowerCase()]&&<p className="ugi-ops-error" role="alert">{errors[tab.toLowerCase()]} <button type="button" onClick={()=>setTick(x=>x+1)}>Retry</button></p>}
      {tab==='Summary'&&<>
        {busy.summary&&!summary&&<p className="ugi-ops-wait">Reading operational records…</p>}
        {summary&&<>
          <div className="ugi-ops-metrics">
            {[
              ['Registered today',summary.measures?.registeredToday],
              ['New · last 7 days',summary.measures?.registeredLast7Days],
              ['Recorded field events · 24h',summary.measures?.activityLast24Hours],
              ['Connected team',`${number(summary.presence?.online)} / ${number(summary.presence?.total)}`]
            ].map(([label,value])=><div key={label} className="ugi-ops-metric"><span>{label}</span><strong>{typeof value==='number'?number(value):value}</strong></div>)}
          </div>
          <div className="ugi-ops-context">
            <div className="ugi-ops-panel"><span className="ugi-eyebrow">ATTENTION REQUIRED</span><h3>Operational signals</h3>
              {summary.alerts?.length?summary.alerts.map(alert=><div className="ugi-ops-alert" key={alert.kind}>
                <span className={alert.severity==='warning'?'ugi-ops-alert-mark warning':'ugi-ops-alert-mark'}/>
                <div><strong>{alert.label}</strong><p>{alert.detail}</p></div><b>{number(alert.count)}</b>
              </div>):<p>No subscription-expiry or missing-state signals in your authorized records.</p>}
              <small>{summary.alertDefinition}</small>
            </div>
            <div className="ugi-ops-panel"><span className="ugi-eyebrow">DATA CONFIDENCE</span><h3>What these numbers mean</h3>
              <p>Connection status is based on a recent authenticated heartbeat. It does not prove attendance, active work, or physical location.</p>
              <p>Field event totals include only FDC records saved in the field-activity collection. Subscription alerts are derived from saved company records.</p>
              <div className="ugi-ops-updated">Last checked: {stamp(summary.generatedAt)} WAT</div>
            </div>
          </div>
        </>}
      </>}
      {tab==='Timeline'&&<div className="ugi-ops-panel">
        <div className="ugi-ops-panel-head"><div><span className="ugi-eyebrow">AUDITABLE FIELD EVENT FEED</span><h3>Recent recorded FDC activity</h3></div>
          <div className="ugi-ops-periods" role="group" aria-label="Timeline period">{periods.map(item=><button key={item.days} type="button" aria-pressed={days===item.days} onClick={()=>{setDays(item.days);setPage(1);setTimeline(null);}}>{item.label}</button>)}</div>
        </div>
        {busy.timeline&&<p className="ugi-ops-wait">Updating the verified event timeline…</p>}
        {!busy.timeline&&timeline?.items?.length===0&&<p>No recorded FDC activity in this time window.</p>}
        <div className="ugi-ops-feed">{timeline?.items?.map(event=><div className="ugi-ops-event" key={`${event.id}-${event.createdAt}`}>
          <span className="ugi-ops-event-point"/><div><strong>{String(event.type||'Field activity').replaceAll('_',' ')}</strong><small>FDC {event.staffId} · {event.status}</small></div>
          <time dateTime={event.createdAt||undefined}>{stamp(event.createdAt)} WAT</time>
        </div>)}</div>
        <div className="ugi-ops-pagination"><span>Page {page}</span><button type="button" disabled={page<=1||busy.timeline} onClick={()=>setPage(p=>p-1)}>Previous</button><button type="button" disabled={!timeline?.hasMore||busy.timeline} onClick={()=>setPage(p=>p+1)}>Next</button></div>
        <p className="ugi-ops-note">{timeline?.definition||'Other staff activities, sales, and customer visits are not inferred from this field event collection.'}</p>
      </div>}
      {tab==='Presence'&&<div className="ugi-ops-panel">
        <div className="ugi-ops-panel-head"><div><span className="ugi-eyebrow">AUTHENTICATED CONNECTIONS</span><h3>Team presence</h3></div><strong className="ugi-ops-present">{number(presence?.onlineTotal)} connected / {number(presence?.totalAuthorized)} authorized</strong></div>
        {busy.presence&&<p className="ugi-ops-wait">Checking team connections…</p>}
        {!busy.presence&&presence?.members?.length===0&&<p>No currently verified team connections.</p>}
        <div className="ugi-ops-people">{presence?.members?.map(person=><div className="ugi-ops-person" key={person.id}>
          <span className="ugi-ops-avatar" aria-hidden="true">{String(person.name||'U').slice(0,1).toUpperCase()}</span>
          <div><strong>{person.name||person.id}</strong><small>{person.role||role} · {person.stateCode||'State unassigned'} · Last heartbeat {stamp(person.lastSeenAt)} WAT</small></div>
          <span className="ugi-ops-online">Connected</span>
        </div>)}</div>
        {presence?.hasMore&&<p className="ugi-ops-note">Showing the 60 most recently connected authorized accounts.</p>}
        <p className="ugi-ops-note">{presence?.definition||'Presence is based on authentication heartbeats, not GPS.'}</p>
      </div>}
    </>}
  </section>;
}
